// Shared loading-skeleton primitives. Use animate-pulse with a theme-aware
// surface tint so the shimmer works in both light and dark mode. Pages should
// reach for these instead of inventing bespoke bg-slate-200 blocks.
//
// DESIGN.md §8b (calm content): skeletons render in place of the value, with
// the same approximate dimensions, so the layout doesn't shift when real data
// arrives.

interface SkeletonProps {
  className?: string
  /** Aria label for screen readers — defaults to "Lädt…". */
  label?: string
}

/** Generic shimmer box. Caller controls dimensions via className. */
export function Skeleton({ className = '', label = 'Lädt…' }: SkeletonProps) {
  return (
    <span
      role="status"
      aria-label={label}
      className={`inline-block motion-safe:animate-pulse rounded-md bg-on-surface-variant/15 ${className}`}
    />
  )
}

interface SkeletonTextProps {
  /** Number of lines to render. Defaults to 1. */
  lines?: number
  /** Tailwind width class applied to the last line. Mimics paragraph trail-off. */
  lastWidth?: string
  className?: string
}

/** Multi-line text skeleton — useful for descriptions or long values. */
export function SkeletonText({ lines = 1, lastWidth = 'w-2/3', className = '' }: SkeletonTextProps) {
  return (
    <div className={`space-y-1.5 ${className}`} aria-busy="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          className={`h-3 ${i === lines - 1 ? lastWidth : 'w-full'}`}
        />
      ))}
    </div>
  )
}

interface SkeletonRowProps {
  /** Number of cells in the row. */
  cols: number
  /** Tailwind class applied to the row container — usually a grid template. */
  className?: string
  /** Per-cell height. Defaults to h-4. */
  cellHeight?: string
}

/**
 * Table-shaped row skeleton. Caller provides the grid template via className
 * so the row matches the live data's column widths exactly.
 *
 * Example:
 *   <SkeletonRow cols={4} className="grid grid-cols-[1fr_100px_100px_100px] gap-3 px-3 py-2.5" />
 */
export function SkeletonRow({ cols, className = '', cellHeight = 'h-4' }: SkeletonRowProps) {
  return (
    <div className={className} aria-busy="true">
      {Array.from({ length: cols }, (_, i) => (
        <Skeleton key={i} className={`${cellHeight} ${i === 0 ? 'w-3/4' : 'w-full'}`} />
      ))}
    </div>
  )
}
